import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import Trans from 'next-translate/Trans';
import useLocale from 'next-translate/useTranslation';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useMemo,
} from 'react';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';

import { useAnalytics } from '@/contexts/Analytics';

export { tone };

/**
 * The auth screens' building blocks (/login, the password pages and
 * AuthModal), in the palette of the landing pages (`agents/primitives`) and
 * the shapes of the app kit: rounded-xl fields, pill buttons.
 */

/** The `login` namespace. A missing key falls back to the English key with
 * its {{placeholders}} filled in (next-translate returns it raw otherwise). */
export function useAuthT() {
  const { t, lang } = useLocale('login');
  return useMemo(
    () => ({
      lang,
      t: (key: string, query?: Record<string, unknown>) =>
        t(key, query, { default: key }),
    }),
    [t, lang],
  );
}

export const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:focus-visible:outline-primary-400';

/**
 * Text fields. 16 px on phones so iOS doesn't zoom in on focus; `plaintext`
 * keeps a Latin email reading left to right on Arabic pages.
 */
export const fieldClass = clsxm(
  'block h-12 w-full rounded-xl border-0 bg-white px-3.5 text-base text-[#10101c] shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-inset ring-[#0f0f28]/[0.14] transition-[box-shadow,background-color] [unicode-bidi:plaintext] placeholder:text-[#9b9bb0] hover:ring-[#0f0f28]/25 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 sm:h-11 sm:text-[15px]',
  'dark:bg-white/[0.05] dark:text-white dark:shadow-none dark:ring-white/[0.13] dark:placeholder:text-[#6e6e8c] dark:hover:ring-white/25 dark:focus:ring-primary-400',
);

export const fieldInvalid =
  'ring-red-500/70 hover:ring-red-500/80 focus:ring-red-500 dark:ring-red-400/60 dark:hover:ring-red-400/70 dark:focus:ring-red-400';

export const labelClass = clsxm(
  'block text-[14px] font-medium leading-5',
  tone.ink,
);

export const linkClass = clsxm(
  'rounded font-medium text-primary-700 underline-offset-2 hover:underline dark:text-primary-300',
  focusRing,
);

type ButtonVariant = 'primary' | 'secondary' | 'quiet';

export function buttonClass(variant: ButtonVariant = 'primary') {
  return clsxm(
    'relative inline-flex h-12 w-full select-none items-center justify-center gap-2 rounded-full px-5 text-[15px] transition-[background-color,box-shadow,color,opacity] duration-150 disabled:cursor-not-allowed sm:h-11',
    variant === 'primary' &&
      'bg-primary-600 font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_24px_-12px_rgba(79,70,229,0.85)] hover:bg-primary-700 disabled:opacity-60 disabled:hover:bg-primary-600 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]',
    variant === 'secondary' &&
      'bg-white font-medium text-[#10101c] shadow-[0_1px_2px_rgba(16,16,40,0.06)] ring-1 ring-inset ring-[#0f0f28]/[0.14] hover:bg-[#fafafd] hover:ring-[#0f0f28]/25 disabled:opacity-60 dark:bg-white/[0.06] dark:text-white dark:shadow-none dark:ring-white/[0.14] dark:hover:bg-white/[0.1] dark:hover:ring-white/25',
    variant === 'quiet' &&
      'h-auto w-auto px-0 text-[14px] font-medium text-primary-700 hover:underline disabled:opacity-60 sm:h-auto dark:text-primary-300',
    focusRing,
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      aria-hidden='true'
      className={clsxm('h-4 w-4 animate-spin', className)}
    >
      <circle
        cx='12'
        cy='12'
        r='9'
        stroke='currentColor'
        strokeOpacity='0.25'
        strokeWidth='3'
      />
      <path
        d='M21 12a9 9 0 0 0-9-9'
        stroke='currentColor'
        strokeWidth='3'
        strokeLinecap='round'
      />
    </svg>
  );
}

type AuthButtonProps = ComponentPropsWithoutRef<'button'> & {
  variant?: ButtonVariant;
  /** Shows a spinner next to the label (the label says what is happening). */
  loading?: boolean;
};

export const AuthButton = forwardRef<HTMLButtonElement, AuthButtonProps>(
  function AuthButton(
    {
      variant = 'primary',
      loading = false,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        aria-busy={loading || undefined}
        className={clsxm(
          buttonClass(variant),
          // Busy reads as working, not as unavailable: full colour.
          loading && 'cursor-wait disabled:cursor-wait disabled:opacity-100',
          className,
        )}
        {...rest}
      >
        {loading && <Spinner />}
        {children}
      </button>
    );
  },
);

const NOTICE_STYLES = {
  error: {
    box: 'bg-red-50 text-red-900 ring-red-600/15 dark:bg-red-500/10 dark:text-red-100 dark:ring-red-400/20',
    icon: 'text-red-600 dark:text-red-300',
    Icon: ExclamationCircleIcon,
  },
  warning: {
    box: 'bg-amber-50 text-amber-950 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-50 dark:ring-amber-300/20',
    icon: 'text-amber-600 dark:text-amber-300',
    Icon: ExclamationIcon,
  },
  success: {
    box: 'bg-emerald-50 text-emerald-950 ring-emerald-600/15 dark:bg-emerald-400/10 dark:text-emerald-50 dark:ring-emerald-300/20',
    icon: 'text-emerald-600 dark:text-emerald-300',
    Icon: CheckCircleIcon,
  },
  info: {
    box: 'bg-primary-50 text-[#1e1b4b] ring-primary-600/15 dark:bg-primary-400/10 dark:text-primary-50 dark:ring-primary-300/20',
    icon: 'text-primary-600 dark:text-primary-300',
    Icon: InformationCircleIcon,
  },
} as const;

/**
 * An inline message. Errors are announced at once (role=alert), the rest
 * politely (role=status).
 */
export function Notice({
  kind,
  title,
  children,
  action,
  className,
  id,
}: {
  kind: keyof typeof NOTICE_STYLES;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  id?: string;
}) {
  const style = NOTICE_STYLES[kind];
  return (
    <div
      id={id}
      role={kind === 'error' ? 'alert' : 'status'}
      className={clsxm(
        'flex gap-3 rounded-2xl px-4 py-3.5 text-[14px] leading-[1.45] ring-1 ring-inset',
        style.box,
        className,
      )}
    >
      <style.Icon
        aria-hidden='true'
        className={clsxm('mt-px h-5 w-5 flex-none', style.icon)}
      />
      <div className='min-w-0 flex-1'>
        {title && <p className='font-semibold'>{title}</p>}
        {children && (
          <div className={clsxm(title ? 'mt-0.5 opacity-90' : undefined)}>
            {children}
          </div>
        )}
        {action && <div className='mt-2.5'>{action}</div>}
      </div>
    </div>
  );
}

/** "or" between the provider buttons and the email form. */
export function OrDivider({ label }: { label: string }) {
  return (
    <div className='flex items-center gap-3' role='separator'>
      <span className='h-px flex-1 bg-[#0f0f28]/[0.09] dark:bg-white/10' />
      <span className={clsxm('text-[13px] font-medium', tone.quiet)}>
        {label}
      </span>
      <span className='h-px flex-1 bg-[#0f0f28]/[0.09] dark:bg-white/10' />
    </div>
  );
}

/** Google's multicolour "G" (keep its colours, per Google's brand rules). */
export function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true' className={className}>
      <path
        fill='#4285F4'
        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
      />
      <path
        fill='#34A853'
        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
      />
      <path
        fill='#FBBC05'
        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
      />
      <path
        fill='#EA4335'
        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
      />
    </svg>
  );
}

export function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='#1877F2'
      aria-hidden='true'
      className={className}
    >
      <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
    </svg>
  );
}

export function ShieldGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      aria-hidden='true'
      className={clsxm('h-4 w-4 flex-none', className)}
    >
      <path
        strokeLinejoin='round'
        d='M10 2.5 4 4.75v4.5c0 3.65 2.55 6.9 6 7.75 3.45-.85 6-4.1 6-7.75v-4.5L10 2.5Z'
      />
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='m7.4 10 1.8 1.8 3.4-3.6'
      />
    </svg>
  );
}

/** Large round icon at the top of a status screen (check your inbox...). */
export function StatusIcon({
  children,
  kind = 'brand',
}: {
  children: ReactNode;
  kind?: 'brand' | 'warning' | 'success';
}) {
  return (
    <span
      aria-hidden='true'
      className={clsxm(
        'flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-inset [&>svg]:h-7 [&>svg]:w-7',
        kind === 'brand' &&
          'bg-primary-50 text-primary-600 ring-primary-600/15 dark:bg-primary-400/10 dark:text-primary-300 dark:ring-primary-300/20',
        kind === 'warning' &&
          'bg-amber-50 text-amber-600 ring-amber-600/15 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-300/20',
        kind === 'success' &&
          'bg-emerald-50 text-emerald-600 ring-emerald-600/15 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-300/20',
      )}
    >
      {children}
    </span>
  );
}

/** The screen's heading and the line under it. */
export function AuthHeading({
  title,
  children,
  icon,
  align = 'start',
  size = 'lg',
}: {
  title: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  align?: 'start' | 'center';
  /** `md` in the modal, where the page size would wrap the title. */
  size?: 'lg' | 'md';
}) {
  return (
    <div className={clsxm(align === 'center' && 'text-center')}>
      {icon && (
        <div
          className={clsxm('mb-5', align === 'center' && 'flex justify-center')}
        >
          {icon}
        </div>
      )}
      <h1
        className={clsxm(
          'font-semibold leading-[1.15] tracking-[-0.02em] [text-wrap:balance]',
          size === 'lg'
            ? 'text-[1.75rem] sm:text-[2rem]'
            : 'text-[1.5rem] sm:text-[1.625rem]',
          '[&:lang(ar)]:tracking-normal [&:lang(ja)]:tracking-normal [&:lang(ko)]:tracking-normal [&:lang(th)]:tracking-normal [&:lang(zh)]:tracking-normal',
          tone.ink,
        )}
      >
        {title}
      </h1>
      {children && (
        <div className={clsxm('mt-2.5 text-[15px] leading-6', tone.body)}>
          {children}
        </div>
      )}
    </div>
  );
}

export type AuthSurface =
  | 'page'
  | 'modal'
  | 'forgot_password'
  | 'reset_password'
  | 'link_error'
  | 'mfa';

export type AuthMode = 'sign_in' | 'sign_up' | 'reset' | 'resend';

export type AuthMethod =
  | 'google'
  | 'facebook'
  | 'email'
  | 'magic_link'
  | 'resend'
  | 'reset_link'
  | 'new_password'
  | 'authenticator';

/**
 * Auth events. Autocapture is off in PostHog, so without these nobody can see
 * which button people press or why a sign-in fails. `Auth Started` fires when
 * a visitor picks a way in (before any network call), `Auth Error` when an
 * error is shown, with a reason code (never the raw text, which can hold an
 * email address). Sign-ups themselves stay `Sign Up Started` /
 * `Sign Up Completed`.
 */
export function useAuthTrack(surface: AuthSurface) {
  const analytics = useAnalytics();
  return useMemo(
    () => ({
      started: (props: { mode: AuthMode; method: AuthMethod }) =>
        analytics.track('Auth Started', { surface, ...props }),
      error: (props: { mode: AuthMode; reason: string; method?: AuthMethod }) =>
        analytics.track('Auth Error', { surface, ...props }),
      modeSwitched: (to: AuthMode) =>
        analytics.track('Auth Mode Switched', { surface, to }),
    }),
    [analytics, surface],
  );
}

/** "We sent a confirmation link to <email>. Open it to finish…" */
export function SentConfirmation({ email }: { email: string }) {
  return (
    <Trans
      i18nKey='We sent a confirmation link to <0>{{email}}</0>. Open it to finish creating your account.'
      ns='login'
      defaultTrans='We sent a confirmation link to <0>{{email}}</0>. Open it to finish creating your account.'
      values={{ email }}
      components={[
        <strong
          key='email'
          dir='ltr'
          className={clsxm('font-semibold [overflow-wrap:anywhere]', tone.ink)}
        />,
      ]}
    />
  );
}
